<template>
  <div class="profile-edit-menu relative flex flex-col z-[1]">
    <Button
      class="max-w-[270px] justify-between"
      variant="outlined"
      size="small"
      :value="dropdownText"
      :aria-expanded="`${buttonToggled}`"
      @click.prevent="toggleMenu"
    >
      <CaretIcon :class="iconDirection" color="#4B4C52" />
    </Button>

    <Card v-if="buttonToggled" class="max-w-[270px] w-full top-36">
      <p class="text-overline capitalize mb-12">My services</p>
      <ul class="flex flex-col gap-16 mb-16">
        <li v-for="profile in completedProfiles" :key="profile.type">
          <a
            class="flex items-center gap-8"
            :href="viewProfilePath(profile.type)"
            :aria-label="`${!profile.visible ? 'Hidden' : ''} ${profile.displayName} profile`"
          >
            <component :is="profile.icon" :width="32" :height="32" />
            <p class="text-subtitle !font-medium mb-4 ml-8 capitalize">{{ profile.displayName }}</p>
            <password-hide-outline-icon v-if="!profile.visible" class="ml-auto" :width="16" :height="16" color="#8A8A8F" />
          </a>
        </li>
      </ul>

      <ul class="flex flex-col gap-16">
        <li v-for="profile in incompleteProfiles" :key="profile.type">
          <a
            class="flex items-center gap-8 bg-grey-background-contrast rounded-xl pl-12 py-8"
            :href="getNewProfileUrl(profile.type)"
          >
            <add-outline-icon :width="32" :height="32" color="#6E6F74" />
            <p class="text-subtitle !font-medium mb-4 text-grey-text-subdued">Add {{ profile.displayName }}</p>
          </a>
        </li>
      </ul>
    </Card>
  </div>
</template>

<script lang="ts">
// Components
import Button from '@/components/button/Button.vue';
import Card from '@/components/card/Card.vue';

// Icons
import CaretIcon from '@/graphics/icons/CaretIcon.vue';
import BabysitterIllustration from '@/graphics/illustrations/BabysitterIllustration.vue';
import NannyIllustration from '@/graphics/illustrations/NannyIllustration.vue';
import PetCareIllustration from '@/graphics/illustrations/PetCareIllustration.vue';
import SeniorCareIllustration from '@/graphics/illustrations/SeniorCareIllustration.vue';
import HousekeepingIllustration from '@/graphics/illustrations/HousekeepingIllustration.vue';
import PasswordHideOutlineIcon from '@/graphics/icons/PasswordHideOutlineIcon.vue';
import AddOutlineIcon from '@/graphics/icons/AddOutlineIcon.vue';

// GraphQL
import { sendSegmentEvent } from '@/lib/segment';

export default {
  name: 'ProfileEditMenu',
  components: {
    // Components
    Button,
    Card,

    // Icons
    CaretIcon,
    BabysitterIllustration,
    NannyIllustration,
    PetCareIllustration,
    SeniorCareIllustration,
    HousekeepingIllustration,
    PasswordHideOutlineIcon,
    AddOutlineIcon,
  },
  props: {
    careTypeForLabel: {
      type: String,
      default: '',
      required: false,
    },
    profileAccountData: {
      type: Object,
      required: true,
    },
  },
  data(): any {
    return {
      buttonToggled: false,
    };
  },
  computed: {
    dropdownText(): string {
      return this.profileData.find((profile) => profile.type === this.careTypeForLabel)?.displayName || 'Edit profile';
    },
    iconDirection(): string {
      return this.buttonToggled ? 'transform rotate-180' : '';
    },
    profileData(): any {
      return [
        {
          type: 'babysitter',
          displayName: 'babysitter',
          complete: this.profileAccountData?.profiles?.babysitter?.profileCompleted || false,
          visible: this.profileAccountData?.profiles?.babysitter?.profileVisible || false,
          icon: BabysitterIllustration,
        },
        {
          type: 'nanny',
          displayName: 'nanny',
          complete: this.profileAccountData?.profiles?.nanny?.profileCompleted || false,
          visible: this.profileAccountData?.profiles?.nanny?.profileVisible || false,
          icon: NannyIllustration,
        },
        {
          type: 'pet_care',
          displayName: 'pet care',
          complete: this.profileAccountData?.profiles?.petCare?.profileCompleted || false,
          visible: this.profileAccountData?.profiles?.petCare?.profileVisible || false,
          icon: PetCareIllustration,
        },
        {
          type: 'companion_care',
          displayName: 'companion care',
          complete: this.profileAccountData?.profiles?.companionCare?.profileCompleted || false,
          visible: this.profileAccountData?.profiles?.companionCare?.profileVisible || false,
          icon: SeniorCareIllustration,
        },
        {
          type: 'housekeeping',
          displayName: 'housekeeping',
          complete: this.profileAccountData?.profiles?.housekeeping?.profileCompleted || false,
          visible: this.profileAccountData?.profiles?.housekeeping?.profileVisible || false,
          icon: HousekeepingIllustration,
        },
      ];
    },
    completedProfiles(): any {
      return this.profileData.filter((profile) => profile.complete === true);
    },
    incompleteProfiles(): any {
      return this.profileData.filter((profile) => profile.complete === false);
    },
    profileOptionButtonClasses(): string {
      return 'text-xs !tracking-normal !leading-none font-regular normal-case primary-focus !p-0';
    },
  },
  mounted() {
    document.addEventListener(
      'click',
      ({ target }) => {
        if ((target as HTMLElement).closest('.profile-edit-menu, .adventure-active-modal') !== null) return;
        this.buttonToggled = false;
      },
      false
    );
  },
  methods: {
    toggleMenu(): void {
      this.buttonToggled = !this.buttonToggled;
    },
    handleEditProfileClick(type: string): void {
      sendSegmentEvent('Sitter Profile Edit Profile Clicked', {
        care_type: type,
      });
    },
    viewProfilePath(type): string {
      const { id } = this.profileAccountData;
      return `/provider/profile/${type}/${id}`;
    },
    getNewProfileUrl(type): string {
      const typeToUrlMap = {
        babysitter: '/sitter/signup/profile/steps',
        nanny: '/sitter/signup/profile/steps',
        pet_care: '/sitter/signup/profile/pet-care',
        companion_care: '/sitter/signup/profile/companion-care',
        housekeeping: '/sitter/signup/profile/housekeeping',
      };

      const params = new URLSearchParams();
      params.append('additionalProfile', 'true');
      params.append('careType', type);

      return `${typeToUrlMap[type]}?${params.toString()}`;
    },
  },
};
</script>

<style lang="scss" scoped>
.card {
  position: absolute !important;
}
</style>
